import React, { useState } from 'react';
import { Bot, X, RefreshCw, Sparkles, AlertCircle } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { icons as LucideIcons } from 'lucide-react';
import { aiApi } from '../../services/api';

export default function FloatingAIAssistant({ campaignData, analyzeFn = aiApi.analyzeReport }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  const runAnalysis = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await analyzeFn(campaignData);
      setAnalysis(response.data);
      setGeneratedAt(new Date());
    } catch (err) {
      setError(err.message || 'Erro ao analisar os dados.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = async () => {
    setIsOpen(true);
    if (!analysis && !isLoading) {
      await runAnalysis();
    }
  };

  const handleRegenerate = async () => {
    setAnalysis(null);
    await runAnalysis();
  };

  const colorClassMap = {
    red: 'text-red-700 bg-red-50 border border-red-200 dark:bg-red-900/30 dark:text-red-300',
    green: 'text-emerald-700 bg-emerald-50 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300',
    blue: 'text-blue-700 bg-blue-50 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300',
    yellow: 'text-amber-700 bg-amber-50 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300',
    purple: 'text-purple-700 bg-purple-50 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-300',
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-40 group flex items-center gap-0 bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-full shadow-[0_8px_30px_rgba(139,92,246,0.4)] hover:shadow-[0_8px_40px_rgba(139,92,246,0.6)] transition-all duration-300 hover:scale-105 active:scale-95 overflow-hidden"
        title="Assistente IA"
      >
        <span className="flex items-center justify-center w-14 h-14">
          <Bot size={24} className="group-hover:rotate-12 transition-transform duration-300" />
        </span>
        <span className="max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-[160px] transition-all duration-300 ease-in-out pr-0 group-hover:pr-5 font-semibold text-sm">
          Gerar Insights IA
        </span>
      </button>

      {/* Modal */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
          onClick={e => { if (e.target === e.currentTarget) setIsOpen(false); }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <Sparkles size={18} />
                </div>
                <div>
                  <h2 className="text-base font-bold leading-tight">Análise por Inteligência Artificial</h2>
                  {generatedAt && !isLoading && (
                    <p className="text-xs text-white/70">
                      Gerado às {generatedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {analysis && !isLoading && (
                  <button
                    onClick={handleRegenerate}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-xs font-semibold transition-colors"
                    title="Regenerar análise"
                  >
                    <RefreshCw size={13} />
                    Regenerar
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 flex items-center justify-center bg-white/15 hover:bg-white/25 rounded-lg transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6 min-h-0 custom-scrollbar">

              {/* Loading State */}
              {isLoading && (
                <div className="flex flex-col items-center justify-center py-20 gap-6">
                  <div className="relative w-20 h-20">
                    <div className="absolute inset-0 rounded-full border-4 border-purple-100 border-t-purple-500 animate-spin" />
                    <div className="absolute inset-3 rounded-full border-4 border-indigo-100 border-b-indigo-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }} />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Bot size={24} className="text-purple-500 animate-pulse" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-base font-semibold text-gray-800 dark:text-white">Analisando seus dados...</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-xs">
                      Nossa IA está identificando padrões, oportunidades e pontos de atenção no seu relatório.
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    {[0, 1, 2].map(i => (
                      <div
                        key={i}
                        className="w-2 h-2 rounded-full bg-purple-400 animate-bounce"
                        style={{ animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Error State */}
              {!isLoading && error && (
                <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
                  <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center">
                    <AlertCircle className="text-red-500" size={28} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800 dark:text-white">Falha ao gerar análise</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{error}</p>
                  </div>
                  <button
                    onClick={handleRegenerate}
                    className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white rounded-xl text-sm font-semibold hover:bg-purple-700 transition-colors"
                  >
                    <RefreshCw size={14} />
                    Tentar Novamente
                  </button>
                </div>
              )}

              {/* Analysis Content */}
              {!isLoading && analysis && (
                <div className="prose prose-sm max-w-none dark:prose-invert
                  prose-headings:font-bold prose-headings:text-gray-900 dark:prose-headings:text-white
                  prose-h2:text-xl prose-h2:border-b prose-h2:border-gray-100 dark:prose-h2:border-gray-700 prose-h2:pb-2 prose-h2:mb-4 prose-h2:mt-8 prose-h2:first:mt-0
                  prose-h3:text-base prose-h3:text-gray-800 dark:prose-h3:text-gray-100 prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-gray-700 dark:prose-p:text-gray-300 prose-p:leading-relaxed
                  prose-li:text-gray-700 dark:prose-li:text-gray-300 prose-li:my-1 prose-ul:my-3 prose-li:leading-relaxed
                  prose-strong:text-gray-900 dark:prose-strong:text-white
                  prose-code:text-purple-700 prose-code:bg-purple-50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:font-mono">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      icon({ node, ...props }) {
                        const iconName = node?.properties?.name || props.name;
                        if (iconName) {
                          let IconComp = LucideIcons[iconName];
                          if (!IconComp) {
                            const key = Object.keys(LucideIcons).find(k => k.toLowerCase() === iconName.toLowerCase());
                            if (key) IconComp = LucideIcons[key];
                          }
                          if (IconComp) {
                            return <IconComp className="inline-block w-5 h-5 mx-1 mb-0.5 text-purple-600 dark:text-purple-400 align-middle" />;
                          }
                        }
                        return null;
                      },
                      highlight({ node, children, ...props }) {
                        const color = node?.properties?.color || props.color || 'purple';
                        const mapped = colorClassMap[color.toLowerCase()] || colorClassMap.purple;
                        return (
                          <span className={`inline-flex items-center font-semibold text-sm px-2 py-0.5 rounded-md ${mapped}`}>
                            {children}
                          </span>
                        );
                      },
                      h2({ children }) {
                        return (
                          <h2 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700 pb-2 mb-4 mt-8 first:mt-0">
                            <span className="w-1 h-5 bg-purple-500 rounded-full shrink-0" />
                            {children}
                          </h2>
                        );
                      },
                      blockquote({ children }) {
                        return (
                          <blockquote className="border-l-4 border-purple-400 bg-purple-50 dark:bg-purple-900/20 pl-4 py-2 pr-3 rounded-r-xl my-4 text-gray-700 dark:text-gray-300 italic">
                            {children}
                          </blockquote>
                        );
                      },
                      table({ children }) {
                        return (
                          <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 my-4">
                            <table className="w-full text-sm">{children}</table>
                          </div>
                        );
                      },
                      th({ children }) {
                        return <th className="px-4 py-2 bg-gray-50 dark:bg-gray-800 text-left text-xs font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider">{children}</th>;
                      },
                      td({ children }) {
                        return <td className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 text-gray-700 dark:text-gray-300">{children}</td>;
                      },
                    }}
                  >
                    {analysis}
                  </ReactMarkdown>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex items-center justify-between shrink-0">
              <p className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1.5">
                <Bot size={12} />
                Análise gerada por IA — revise antes de tomar decisões
              </p>
              <button
                onClick={() => setIsOpen(false)}
                className="px-5 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-xl text-sm font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
